import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Slider, ValueInputField } from '@crabs-ai/ds-react';
import { changeItems } from '../../state/actions/change-items';
import { useCurrentStateAsRef, useWriteContext } from '../../utils/use-context';
import { findAllLinkedItems } from '../../utils/find-linked-items';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { AudioItem } from '../../items/audio/audio-item-type';
import { VideoItem } from '../../items/video/video-item-type';
import { GifItem } from '../../items/gif/gif-item-type';
import { WebpItem } from '../../items/webp/webp-item-type';
import { useSelectionValues } from '../utils/use-selection-values';
import {
  MAX_PLAYBACK_RATE,
  MIN_PLAYBACK_RATE,
  computePlaybackRateChange,
} from './playback-rate-apply';
import { useTranslations } from 'next-intl';

const PlaybackRateControlsUnmemoized: React.FC<{
  playbackRate: number;
  itemId: string;
}> = ({ playbackRate, itemId }) => {
  const t = useTranslations();

  const { setState } = useWriteContext();
  // tracks/items/링크 여부는 구독 없이 ref로 읽어 불필요한 리렌더를 피한다.
  const stateAsRef = useCurrentStateAsRef();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<
    AudioItem | VideoItem | GifItem | WebpItem,
    number
  >(itemId, (item) => item.playbackRate);

  // 드래그 시작 시 최신 선택을 읽기 위한 ref 미러(렌더 phase write, SSR 안전).
  const targetIdsRef = useRef(targetIds);
  targetIdsRef.current = targetIds;

  // 드래그 세션: 시작 시 고정한 대상 집합(targets)과 각 대상의 원본 구간(sourceFrames).
  // - sourceFrames 고정: floor 누적으로 per-clip 천장이 드리프트하는 것을 방지.
  // - targets 고정: 링크 ON이면 findAllLinkedItems로 음성↔영상 등 링크 클립을 포함하고
  //   드래그 내내 같은 집합 유지(틱마다 재탐색 시 길이 변화로 매칭이 어긋남).
  // commit / 선택 변경 시 해제.
  const dragSessionRef = useRef<{
    targets: string[];
    sourceFrames: Map<string, number>;
  } | null>(null);

  // 선택 "내용"이 바뀌면 이전 드래그 세션을 해제한다. (배열 참조가 아니라 join 키로
  // 비교 → selectedItems가 동일 내용·새 참조로 갱신돼도 드래그 중 오발동하지 않음)
  const dragSessionResetKey = targetIds.join(',');
  useEffect(() => {
    dragSessionRef.current = null;
  }, [dragSessionResetKey]);

  const setPlaybackRate = useCallback(
    (newPlaybackRate: number, commitToUndoStack: boolean) => {
      const safePlaybackRate = Math.max(MIN_PLAYBACK_RATE, newPlaybackRate);

      // 드래그 한 번 동안 대상 집합과 원본 구간을 1회만 스냅샷한다.
      // (floor 누적으로 천장이 드리프트하거나, 틱마다 링크 재탐색으로 집합이
      //  흔들리는 것을 방지 — 드래그 속도와 무관한 결과 보장)
      if (!dragSessionRef.current) {
        const state = stateAsRef.current;
        const baseTargets = targetIdsRef.current;
        const itemsMap = state?.undoableState.items ?? {};
        // 메인 트랙 링크가 켜져 있으면 링크된 클립(음성↔영상)도 함께 대상에 포함
        const linkedTargets = state?.isLinkingEnabled
          ? findAllLinkedItems({
              targetItemIds: baseTargets,
              tracks: state.undoableState.tracks,
              items: itemsMap,
            })
          : baseTargets;

        // 재생 속도가 있는 클립만 대상으로 둔다.
        // (자막 등은 링크돼도 배속 무관 → 제외. 자막 추종은 기존 메커니즘에 위임)
        const targets = linkedTargets.filter((id) => {
          const target = itemsMap[id];
          return (
            !!target && 'playbackRate' in target && 'durationInFrames' in target
          );
        });

        const sourceFrames = new Map<string, number>();
        for (const id of targets) {
          const target = itemsMap[id];
          if (
            target &&
            'durationInFrames' in target &&
            'playbackRate' in target
          ) {
            sourceFrames.set(id, target.durationInFrames * target.playbackRate);
          }
        }
        // state를 못 읽은 극단 상황엔 baseTargets로 폴백(updater가 현재값으로 처리)
        dragSessionRef.current = {
          targets: targets.length > 0 ? targets : baseTargets,
          sourceFrames,
        };
      }
      const { targets, sourceFrames } = dragSessionRef.current;

      setState({
        update: (state) => {
          return changeItems(state, targets, (i) => {
            // 클립마다 자기 에셋을 조회한다(primary 1개로 고정 금지).
            const itemAsset =
              'assetId' in i
                ? state.undoableState.assets[i.assetId]
                : undefined;
            if (!itemAsset) {
              return i;
            }

            // 드래그 시작 시 고정한 원본 구간(없으면 현재 값으로 폴백)
            const sourceFramesInSegment =
              sourceFrames.get(i.id) ??
              ('durationInFrames' in i && 'playbackRate' in i
                ? i.durationInFrames * i.playbackRate
                : 0);

            if (i.type === 'video' && itemAsset.type === 'video') {
              const { playbackRate: nextRate, durationInFrames: nextDuration } =
                computePlaybackRateChange({
                  sourceFramesInSegment,
                  currentDurationInFrames: i.durationInFrames,
                  currentPlaybackRate: i.playbackRate,
                  requestedRate: safePlaybackRate,
                  assetDurationInSeconds: itemAsset.durationInSeconds,
                  startFromInSeconds: i.videoStartFromInSeconds,
                  fps: state.undoableState.fps,
                  item: i,
                  tracks: state.undoableState.tracks,
                  items: state.undoableState.items,
                });

              if (
                nextRate === i.playbackRate &&
                nextDuration === i.durationInFrames
              ) {
                return i;
              }

              return {
                ...i,
                playbackRate: nextRate,
                durationInFrames: nextDuration,
              };
            }

            if (i.type === 'audio' && itemAsset.type === 'audio') {
              const { playbackRate: nextRate, durationInFrames: nextDuration } =
                computePlaybackRateChange({
                  sourceFramesInSegment,
                  currentDurationInFrames: i.durationInFrames,
                  currentPlaybackRate: i.playbackRate,
                  requestedRate: safePlaybackRate,
                  assetDurationInSeconds: itemAsset.durationInSeconds,
                  startFromInSeconds: i.audioStartFromInSeconds,
                  fps: state.undoableState.fps,
                  item: i,
                  tracks: state.undoableState.tracks,
                  items: state.undoableState.items,
                });

              if (
                nextRate === i.playbackRate &&
                nextDuration === i.durationInFrames
              ) {
                return i;
              }

              return {
                ...i,
                playbackRate: nextRate,
                durationInFrames: nextDuration,
              };
            }

            if (i.type === 'gif' && itemAsset.type === 'gif') {
              if (i.playbackRate === safePlaybackRate) {
                return i;
              }

              // GIF는 자동 루프되므로 에셋 길이 제한 없음
              // 재생 속도 변경 시 현재 duration 유지
              return {
                ...i,
                playbackRate: safePlaybackRate,
              };
            }

            if (i.type === 'webp' && itemAsset.type === 'webp') {
              if (i.playbackRate === safePlaybackRate) {
                return i;
              }

              // WebP는 자동 루프되므로 에셋 길이 제한 없음
              // 재생 속도 변경 시 현재 duration 유지
              return {
                ...i,
                playbackRate: safePlaybackRate,
              };
            }

            // 지원하지 않는 타입이거나 아이템-에셋 타입이 어긋나면 건너뛴다
            // (혼합 타입 멀티 선택 시 throw로 전체가 깨지지 않도록)
            return i;
          });
        },
        commitToUndoStack,
      });

      // 드래그 종료(commit) 시 세션 해제 → 다음 조작은 새 세션으로 시작.
      // setState updater는 위에서 캡처한 지역 변수(targets/sourceFrames)를 쓰므로,
      // updater 실행 전에 ref를 null로 돌려도 in-flight 계산에는 영향이 없다.
      if (commitToUndoStack) {
        dragSessionRef.current = null;
      }
    },
    // tracks/items/링크/선택은 모두 ref 경유로 읽으므로 deps에 둘 필요 없음(stale 없음).
    [setState, stateAsRef],
  );

  const handleSliderChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      const newPlaybackRate = value / 100;
      setPlaybackRate(newPlaybackRate, commitToUndoStack);
    },
    [setPlaybackRate],
  );

  const playbackRatePercent = Math.round(playbackRate * 100);

  return (
    <div>
      <div className="flex w-full items-center gap-3">
        <Slider
          value={[playbackRatePercent]}
          onValueChange={(values) => handleSliderChange(values[0] ?? 0, false)}
          onValueCommit={(values) => handleSliderChange(values[0] ?? 0, true)}
          min={MIN_PLAYBACK_RATE * 100}
          max={MAX_PLAYBACK_RATE * 100}
          step={5}
          className="flex-1"
          isShowTooltip={false}
          aria-label={
            isMixed
              ? `${t('Editor.inspector.playback-speed')}: Mixed`
              : `${t('Editor.inspector.playback-speed')}: ${playbackRate.toFixed(2)}x`
          }
        />
        <ValueInputField
          value={Number(playbackRate.toFixed(2))}
          setValue={(update) =>
            setPlaybackRate(update.num, update.commitToUndoStack)
          }
          min={MIN_PLAYBACK_RATE}
          max={MAX_PLAYBACK_RATE}
          step={0.05}
          isUnit="x"
          hasMixedValue={isMixed}
          accessibilityLabel="Playback Speed"
          className="w-[96px]"
        />
      </div>
    </div>
  );
};

export const PlaybackRateControls = memo(PlaybackRateControlsUnmemoized);
